import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, HostListener, inject } from '@angular/core';
import { ContentChange } from 'ngx-quill/lib/quill-editor.component';
import 'quill-emoji/dist/quill-emoji.js';
// eslint-disable-next-line @typescript-eslint/naming-convention
import Quill, { DeltaOperation, DeltaStatic } from 'quill';
import { FileUploadApiService } from '../../../service/file-upload-api.service';
import { from, map, Observable, switchMap } from 'rxjs';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { Store } from '@skyeng/libs/shared/store';

// @see https://regexr.com/39nr7
const URL_PATTERN = /[(http(s)?)://(www.)?\-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/;

@DecorateUntilDestroy()
@Component({
  selector: 'app-text-editor',
  templateUrl: 'text-editor.html',
  styleUrls: ['text-editor.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextEditorComponent {
  @Input() placeholder = '';
  @Input() content = '';
  @Input() scrollingContainer: string | null = null;
  @Input() hasAutofocus = false;
  @Input() disabled = false;

  @Output() contentChange = new EventEmitter<string>();

  store = new Store({
    isImageLoading: false,
  });

  isImageLoading$ = this.store.select('isImageLoading');

  modules = {
    toolbar: [
      [
        'bold',
        'italic',
        'strike',
        'blockquote',
        'code-block',
        { list: 'ordered' },
        { list: 'bullet' },
        'link',
        'emoji',
        'clean',
      ],
    ],
    clipboard: {
      matchVisual: false,
    },
    'emoji-toolbar': true,
  };

  private fileUploadApiService = inject(FileUploadApiService);

  /**
   * workaround чтобы оверлей поповера с emoji не блокировал скролл страниц, например страницы тикета в ЛКС
   * в стилях к оверлею добавлено point-events: none, а закрытие окна происходит по клику вне поповера вместо оверлея
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const emojiCloseDiv = document.getElementById('emoji-close-div');
    const emojiButton = document.querySelector('.ql-emoji');
    const emojiPalette = document.getElementById('emoji-palette');

    // Проверяем, что клик не произошел по кнопке emoji или внутри палитры
    const target = event.target as Element;
    if (
      target &&
      (emojiButton?.contains(target) ||
        emojiPalette?.contains(target) ||
        target.closest('.ql-emoji') ||
        target.closest('#emoji-palette'))
    ) {
      return;
    }

    if (emojiCloseDiv) {
      const computedStyle = window.getComputedStyle(emojiCloseDiv);

      if (computedStyle.display !== 'none') {
        emojiCloseDiv.click();
      }
    }
  }

  handleContentChanged(newContent: ContentChange): void {
    const content = newContent?.html || '';
    this.contentChange.emit(content);
  }

  init(quill: Quill): void {
    quill.clipboard.addMatcher('IMG', (node, delta) => {
      return this.onPasteImage(node, delta, quill);
    });
    quill.clipboard.addMatcher('PICTURE', (node, delta) => {
      return this.onPasteImage(node, delta, quill);
    });

    // Вставка текста с URL добавляет ссылку
    quill.clipboard.addMatcher(Node.TEXT_NODE, (node: Text, delta) => {
      if (node.data.startsWith('https://') || node.data.startsWith('http://')) {
        const match = URL_PATTERN.exec(node.data);
        if (match === null) {
          return delta;
        }

        return { ...delta, ops: this.getDeltaOps(match) };
      }

      return delta;
    });

    if (this.hasAutofocus) {
      quill.focus();
    }
  }

  private getDeltaOps(match: RegExpExecArray): DeltaOperation[] {
    let ops: DeltaOperation[] = [];

    const inputBefore = match['input'].substring(0, match['index']);
    const inputAfter = match['input'].substring(match['index'] + match[0].length);

    ops = [
      ...ops,
      { insert: inputBefore },
      { insert: match[0], attributes: { link: match[0] } },
      { insert: inputAfter },
    ];

    return ops;
  }

  private onPasteImage(node: HTMLImageElement | HTMLPictureElement, delta: DeltaStatic, quill: Quill): DeltaStatic {
    const src = node?.getAttribute('src');
    this.store.updateFields({ isImageLoading: true });

    // Все что не инлайн изображение пропускаем как есть
    if (!src || !src.startsWith('data:image/')) {
      node.addEventListener('load', () => {
        this.store.updateFields({ isImageLoading: false });
      });
      return delta;
    }

    this.uploadInlineImage(src)
      .pipe(takeUntilDestroyed(this))
      .subscribe(url => {
        const index = quill.getSelection()?.index ?? quill.getLength();
        this.store.updateFields({ isImageLoading: false });
        quill.insertEmbed(index, 'image', url, 'user');
      });

    const Delta = Quill.import('delta');
    return new Delta().insert('');
  }

  private uploadInlineImage(src: string): Observable<string> {
    const allowedImageTypes = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };

    return from(fetch(src).then(res => res.blob())).pipe(
      map(blob => {
        if (!allowedImageTypes[blob.type]) {
          throw new Error('File type is not allowed');
        }

        return new File([blob], `Image${Date.now()}.${allowedImageTypes[blob.type]}`, { type: blob.type });
      }),
      switchMap(file => {
        return this.fileUploadApiService.upload(file);
      }),
      map(({ url }) => url),
    );
  }
}
